import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { PRODUCT_DETAIL_FEATURES } from '../constants';
import { useLanguage } from '../hooks/useLanguage';
import { trackEvent } from '../utils/analytics';
import '../styles/components/ProductDetail.css';

const ProductDetail = ({ productId, Mockup }) => {
  const { t } = useLanguage();
  const product = t.products[productId];
  const features = PRODUCT_DETAIL_FEATURES[productId] || [];

  useEffect(() => {
    if (product?.meta?.title) {
      const previousTitle = document.title;
      document.title = product.meta.title;
      return () => {
        document.title = previousTitle;
      };
    }
    return undefined;
  }, [product]);

  return (
    <main className={`product-detail product-detail--${productId}`}>
      <section className='product-detail-hero'>
        <div className='product-detail-hero-bg' />
        <div className='product-detail-hero-overlay' />
        <div className='container product-detail-hero-content'>
          <div className='product-detail-hero-text'>
            <span className='products-eyebrow'>{product.hero.eyebrow}</span>
            <h1 className='product-detail-hero-title'>{product.hero.title}</h1>
            <p className='product-detail-hero-tagline'>
              {product.hero.tagline}
            </p>
            <p className='product-detail-hero-description'>
              {product.hero.description}
            </p>
            <div className='product-detail-hero-actions'>
              <Link
                to={`/?producto=${productId}#contact`}
                className='product-cta product-cta--primary'
                onClick={() =>
                  trackEvent('cta_click', {
                    cta: 'product_trial',
                    product: productId,
                    location: 'product_detail_hero',
                  })
                }
              >
                {product.hero.ctaTrial}
                <ArrowRight size={16} />
              </Link>
              <Link
                to='/#contact'
                className='product-cta product-cta--ghost'
                onClick={() =>
                  trackEvent('cta_click', {
                    cta: 'product_contact',
                    product: productId,
                    location: 'product_detail_hero',
                  })
                }
              >
                {product.hero.ctaContact}
              </Link>
            </div>
          </div>
          <div className='product-detail-hero-mockup'>
            {Mockup && <Mockup />}
          </div>
        </div>
      </section>

      <section className='product-detail-features section'>
        <div className='container'>
          <div className='product-detail-features-grid'>
            {features.map((feature, index) => {
              const Icon = feature.icon;
              const texts = product.features[feature.id];
              return (
                <article
                  key={feature.id}
                  className='product-detail-feature'
                  style={{ '--feature-index': index }}
                >
                  <div className='product-detail-feature-icon'>
                    <Icon size={22} />
                  </div>
                  <h3 className='product-detail-feature-title'>
                    {texts.title}
                  </h3>
                  <p className='product-detail-feature-description'>
                    {texts.description}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className='product-detail-audience section'>
        <div className='container'>
          <div className='product-detail-audience-inner'>
            <h2 className='product-detail-audience-title'>
              {product.audience.title}
            </h2>
            <ul className='product-detail-audience-list'>
              {product.audience.items.map(item => (
                <li key={item}>
                  <span className='product-detail-audience-check'>
                    <Check size={14} />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className='product-detail-final-cta'>
        <div className='container'>
          <div className='product-detail-final-cta-inner'>
            <h2 className='product-detail-final-cta-title'>
              {product.finalCta.title}
            </h2>
            <p className='product-detail-final-cta-subtitle'>
              {product.finalCta.subtitle}
            </p>
            <Link
              to={`/?producto=${productId}#contact`}
              className='product-cta product-cta--primary product-cta--large'
              onClick={() =>
                trackEvent('cta_click', {
                  cta: 'product_trial',
                  product: productId,
                  location: 'product_detail_final',
                })
              }
            >
              {product.finalCta.ctaTrial}
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ProductDetail;
